import type { PdfBrowserEngine } from "./browser-engine";
import {
  defaultPdfOptions,
  withSamplePreviewHashNavigation,
  type PdfOptions,
} from "./print-document";
import {
  getSamplePreviewDefinitions,
  samplePreviewIds,
  type SamplePreviewId,
} from "./sample-preview-registry";
import {
  samplePreviewSnapshotManifest,
  type SamplePreviewEngineSnapshot,
  type SamplePreviewSampleSnapshot,
} from "./sample-preview-snapshot.generated";

export type {
  SamplePreviewEngineSnapshot,
  SamplePreviewSampleSnapshot,
  SamplePreviewSnapshotManifest,
} from "./sample-preview-snapshot.generated";

export type { SamplePreviewId } from "./sample-preview-registry";
export {
  getSamplePreviewDefinitions,
  samplePreviewIds,
} from "./sample-preview-registry";

/** FNV-1a 32-bit — browser-safe, matches build/verify scripts. */
export function hashSamplePreviewMarkdown(markdown: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < markdown.length; index += 1) {
    hash ^= markdown.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function buildSamplePreviewOptionsKey(options: PdfOptions): string {
  return [
    options.paperSize,
    options.orientation,
    options.margins,
    options.theme,
    options.codeTheme,
    options.diagramTheme,
    String(options.pageNumbers),
    options.fontFamily,
    String(options.fontSize),
    options.diagramFontFamily,
    String(options.diagramFontSize),
    options.codeFontFamily,
    String(options.codeFontSize),
  ].join("\u0001");
}

export const samplePreviewSnapshotOptionsKey = buildSamplePreviewOptionsKey(
  defaultPdfOptions,
);

export function pdfOptionsMatchSampleSnapshot(options: PdfOptions): boolean {
  return (
    buildSamplePreviewOptionsKey(options) === samplePreviewSnapshotOptionsKey
  );
}

export function findSamplePreviewId(
  markdown: string,
): SamplePreviewId | null {
  const hash = hashSamplePreviewMarkdown(markdown);
  for (const id of samplePreviewIds) {
    const entry = samplePreviewSnapshotManifest.samples[id];
    if (entry?.markdownHash === hash) {
      return id;
    }
  }
  return null;
}

function sampleEntryIsUsable(entry: SamplePreviewSampleSnapshot | undefined) {
  return Boolean(
    entry &&
      entry.markdownHash &&
      entry.chrome.pageCount >= 1 &&
      entry.chrome.revision &&
      entry.firefox.pageCount >= 1 &&
      entry.firefox.revision,
  );
}

export function getSamplePreviewSnapshotMeta(
  engine: PdfBrowserEngine,
  markdown: string,
): Omit<SamplePreviewEngineSnapshot, "html"> | null {
  const id = findSamplePreviewId(markdown);
  if (!id) {
    return null;
  }
  const snap = samplePreviewSnapshotManifest.samples[id]?.[engine];
  if (!snap || snap.pageCount < 1 || !snap.revision) {
    return null;
  }
  return { pageCount: snap.pageCount, revision: snap.revision };
}

type SampleHtmlModule = {
  samplePreviewHtml: Record<PdfBrowserEngine, string>;
};

const sampleHtmlLoaders: Record<
  SamplePreviewId,
  () => Promise<SampleHtmlModule>
> = {
  home: () => import("./sample-preview-snapshots/home.generated"),
  resume: () => import("./sample-preview-snapshots/resume.generated"),
  readme: () => import("./sample-preview-snapshots/readme.generated"),
  chatgpt: () => import("./sample-preview-snapshots/chatgpt.generated"),
  gemini: () => import("./sample-preview-snapshots/gemini.generated"),
  mermaid: () => import("./sample-preview-snapshots/mermaid.generated"),
};

const sampleHtmlCache = new Map<
  SamplePreviewId,
  Record<PdfBrowserEngine, string>
>();

export async function loadSamplePreviewSnapshot(
  engine: PdfBrowserEngine,
  markdown: string,
): Promise<SamplePreviewEngineSnapshot | null> {
  const id = findSamplePreviewId(markdown);
  if (!id) {
    return null;
  }
  const meta = samplePreviewSnapshotManifest.samples[id]?.[engine];
  if (!meta || meta.pageCount < 1 || !meta.revision) {
    return null;
  }

  let htmlByEngine = sampleHtmlCache.get(id);
  if (!htmlByEngine) {
    const mod = await sampleHtmlLoaders[id]();
    htmlByEngine = mod.samplePreviewHtml;
    sampleHtmlCache.set(id, htmlByEngine);
  }

  const html = htmlByEngine[engine];
  if (!html) {
    return null;
  }

  return {
    // Snapshots strip scripts after Paged.js; restore hash-link interception so
    // Firefox/Zen does not navigate the iframe to the parent origin.
    html: withSamplePreviewHashNavigation(html),
    pageCount: meta.pageCount,
    revision: meta.revision,
  };
}

/** Sync helper for tests/scripts — prefers cache, else throws if not loaded. */
export function getSamplePreviewSnapshot(
  engine: PdfBrowserEngine,
  markdown: string,
): SamplePreviewEngineSnapshot | null {
  const id = findSamplePreviewId(markdown);
  if (!id) {
    return null;
  }
  const meta = samplePreviewSnapshotManifest.samples[id]?.[engine];
  const html = sampleHtmlCache.get(id)?.[engine];
  if (!meta || meta.pageCount < 1 || !html) {
    return null;
  }
  return {
    html: withSamplePreviewHashNavigation(html),
    pageCount: meta.pageCount,
    revision: meta.revision,
  };
}

export function samplePreviewSnapshotIsCurrent(markdown: string): boolean {
  if (
    samplePreviewSnapshotManifest.optionsKey !==
    samplePreviewSnapshotOptionsKey
  ) {
    return false;
  }
  const definition = getSamplePreviewDefinitions().find(
    (sample) => sample.markdown === markdown,
  );
  if (!definition) {
    return false;
  }
  const entry = samplePreviewSnapshotManifest.samples[definition.id];
  return (
    entry?.markdownHash === hashSamplePreviewMarkdown(markdown) &&
    sampleEntryIsUsable(entry)
  );
}

export function allSamplePreviewSnapshotsAreCurrent(): boolean {
  if (
    samplePreviewSnapshotManifest.optionsKey !==
    samplePreviewSnapshotOptionsKey
  ) {
    return false;
  }
  return getSamplePreviewDefinitions().every((sample) =>
    samplePreviewSnapshotIsCurrent(sample.markdown),
  );
}

export function shouldUseSamplePreviewSnapshot({
  preferSampleSnapshot,
  markdown,
  sampleMarkdown,
  options,
}: {
  preferSampleSnapshot: boolean;
  markdown: string;
  sampleMarkdown: string;
  options: PdfOptions;
}): boolean {
  return (
    preferSampleSnapshot &&
    markdown === sampleMarkdown &&
    pdfOptionsMatchSampleSnapshot(options) &&
    samplePreviewSnapshotIsCurrent(markdown)
  );
}
